// Outreach state unification — see docs/outreach-state-model.md.
// Single source of truth for LeadStatus / LeadContactStatus / LeadKind enums in the frontend.

export const LEAD_STATUS_OPTIONS = [
  { value: 'New',           label: 'New',           description: 'Just added — not yet researched or contacted.' },
  { value: 'Researching',   label: 'Researching',   description: 'Gathering company and contact info.' },
  { value: 'InReview',      label: 'In Review',     description: 'Awaiting human review before qualifying or outreach.' },
  { value: 'Qualified',     label: 'Qualified',     description: 'Reviewed and a good fit — ready for outreach.' },
  { value: 'ReadyForOutreach', label: 'Ready for Outreach', description: 'Contacts discovered, ready to be enrolled in a campaign.' },
  { value: 'Converted',     label: 'Converted',     description: 'Became a customer.' },
  { value: 'Disqualified',  label: 'Disqualified',  description: 'Not a fit — removed from active pipeline.' },
  { value: 'Nurture',       label: 'Nurture',       description: 'Paused or scheduled for later re-engagement.' },
  { value: 'NotInterested', label: 'Not Interested',description: 'Lead explicitly declined.' },
  { value: 'NotRelevant',   label: 'Not Relevant',  description: 'Post-conversation finding that the product does not fit.' },
];

// Deprecated values. Backend no longer writes these — kept for read-side tolerance of legacy rows
// that have not yet been remigrated. Hidden from user-facing dropdowns.
export const LEAD_STATUS_DEPRECATED = ['InOutreach', 'Responded', 'Parked', 'PartnerCandidate', 'Archived'];

export const LEAD_STATUS_COLORS = {
  // Active lifecycle
  New:              { bg: '#f0f9ff', text: '#0369a1', border: '#bae6fd' },
  Researching:      { bg: '#f5f3ff', text: '#6d28d9', border: '#ddd6fe' },
  InReview:         { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa' },
  Qualified:        { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' },
  ReadyForOutreach: { bg: '#ccfbf1', text: '#115e59', border: '#99f6e4' },
  Converted:        { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' },
  // Terminal / paused
  Disqualified:     { bg: '#fee2e2', text: '#991b1b', border: '#fecaca' },
  Nurture:          { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  NotInterested:    { bg: '#fee2e2', text: '#7f1d1d', border: '#fecaca' },
  NotRelevant:      { bg: '#f3f4f6', text: '#4b5563', border: '#e5e7eb' },
  // Legacy fallback (rendered tolerantly, never offered as a choice)
  InOutreach:       { bg: '#fef9c3', text: '#854d0e', border: '#fef08a' },
  Responded:        { bg: '#cffafe', text: '#155e75', border: '#a5f3fc' },
  Archived:         { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' },
  Parked:           { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  PartnerCandidate: { bg: '#ede9fe', text: '#5b21b6', border: '#ddd6fe' },
};

// Default lifecycle whitelist used by automation entry points (mirror of backend default).
export const OUTREACH_ELIGIBLE_LEAD_STATUSES = ['Qualified', 'ReadyForOutreach'];

// Statuses that hide a lead from default workbench filters by default.
export const INACTIVE_LEAD_STATUSES = ['Disqualified', 'Nurture', 'NotInterested', 'NotRelevant', 'Converted'];

// ─── LeadKind ───
export const LEAD_KIND_OPTIONS = [
  { value: 'Customer', label: 'Customer' },
  { value: 'Partner',  label: 'Partner' },
];

// ─── LeadContactStatus ───
export const LEAD_CONTACT_STATUS_OPTIONS = [
  { value: 'Discovered',       label: 'Discovered' },
  { value: 'Recommended',      label: 'Recommended' },
  { value: 'ReadyForOutreach', label: 'Ready for Outreach' },
  { value: 'OutreachActive',   label: 'Outreach Active' },
  { value: 'InConversation',   label: 'In Conversation' },
  { value: 'NotRelevant',      label: 'Not Relevant' },
  { value: 'GenericMailbox',   label: 'Generic Mailbox' },
  { value: 'NoReplyMailbox',   label: 'No-Reply Mailbox' },
  { value: 'Unsubscribed',     label: 'Unsubscribed' },
  { value: 'EmailInvalid',     label: 'Email Invalid' },
  { value: 'Bounced',          label: 'Bounced' },
  { value: 'Paused',           label: 'Paused' },
  { value: 'Excluded',         label: 'Excluded' },
];

export const LEAD_CONTACT_STATUS_COLORS = {
  Discovered:       { bg: '#f3f4f6', text: '#4b5563', border: '#e5e7eb' },
  Recommended:      { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' },
  ReadyForOutreach: { bg: '#ccfbf1', text: '#115e59', border: '#99f6e4' },
  OutreachActive:   { bg: '#fef9c3', text: '#854d0e', border: '#fef08a' },
  InConversation:   { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' },
  NotRelevant:      { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' },
  GenericMailbox:   { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  NoReplyMailbox:   { bg: '#fee2e2', text: '#7f1d1d', border: '#fecaca' },
  Unsubscribed:     { bg: '#fee2e2', text: '#991b1b', border: '#fecaca' },
  EmailInvalid:     { bg: '#fee2e2', text: '#991b1b', border: '#fecaca' },
  Bounced:          { bg: '#fee2e2', text: '#991b1b', border: '#fecaca' },
  Paused:           { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  Excluded:         { bg: '#f3f4f6', text: '#4b5563', border: '#e5e7eb' },
};

// Computed display state (frontend-only, from lead cache fields).
export function deriveOutreachState(lead) {
  if (!lead) return 'NotStarted';
  if (lead.isCompanySuppressed) return 'FullySuppressed';
  if (lead.status === 'Nurture') return 'NurtureScheduled';
  if (lead.status === 'NotInterested') return 'Declined';
  if (lead.status === 'NotRelevant') return 'OutOfScope';
  if (!lead.hasEligibleContact) return 'NoEligibleContacts';
  if (lead.lastInboundDate) return 'InConversation';
  if (lead.lastOutboundDate) return 'Active';
  if (lead.status === 'ReadyForOutreach') return 'Ready';
  return 'NotStarted';
}

export const OUTREACH_STATE_LABELS = {
  NotStarted:         'Not Started',
  Ready:              'Ready',
  Active:             'Active',
  InConversation:     'In Conversation',
  NoEligibleContacts: 'No Eligible Contacts',
  FullySuppressed:    'Fully Suppressed',
  NurtureScheduled:   'Nurture Scheduled',
  Declined:           'Declined',
  OutOfScope:         'Out of Scope',
};

export const OUTREACH_STATE_COLORS = {
  NotStarted:         { bg: '#f3f4f6', text: '#6b7280' },
  Ready:              { bg: '#ccfbf1', text: '#115e59' },
  Active:             { bg: '#fef9c3', text: '#854d0e' },
  InConversation:     { bg: '#dbeafe', text: '#1e40af' },
  NoEligibleContacts: { bg: '#f3f4f6', text: '#6b7280' },
  FullySuppressed:    { bg: '#fee2e2', text: '#991b1b' },
  NurtureScheduled:   { bg: '#fef3c7', text: '#92400e' },
  Declined:           { bg: '#fee2e2', text: '#7f1d1d' },
  OutOfScope:         { bg: '#f3f4f6', text: '#4b5563' },
};
